import { useEffect, useRef } from 'react';
import Link from '@discover-ui/link';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import DismissButton from '../dismissButton';
import illustrationDesktop from './illustration-desktop.svg?url';
import illustrationMobile from './illustration-mobile.svg?url';

export const DINE_NOW_PATH = '/dine-now';
export const DINE_NOW_BANNER_CLOSED_STORAGE_KEY = 'pd7076DineNowBannerClosed';

type DineNowBannerProps = {
  cityId?: string | number | null;
  cityName?: string | null;
  onDismiss: () => void;
};

export default function DineNowBanner({ cityId, cityName, onDismiss }: DineNowBannerProps) {
  const { track } = useAnalytics();
  const viewedCityId = useRef<string | number | null>(null);
  const eventProps = { city_id: cityId, city_name: cityName };

  useEffect(() => {
    if (cityId == null || viewedCityId.current === cityId) return;
    viewedCityId.current = cityId;
    track('dine_now_banner_viewed', { city_id: cityId, city_name: cityName });
  }, [cityId, cityName, track]);

  return (
    <section className="bg-ds-teal-100 sm:to-ds-teal-200 sm:ring-ds-alpha-black-10 relative mx-4 mb-6 mt-4 overflow-hidden rounded-2xl sm:flex sm:bg-gradient-to-r sm:from-white sm:ring-1 sm:ring-inset lg:mx-0 lg:mr-10">
      <picture className="relative block w-full sm:order-last sm:w-1/2 sm:max-w-sm sm:self-stretch">
        {/* 784px is the Tailwind sm screen, so the asset switches with the layout. */}
        <source media="(min-width: 784px)" srcSet={illustrationDesktop} />
        <img
          src={illustrationMobile}
          alt=""
          width={358}
          height={184}
          className="h-auto w-full sm:absolute sm:inset-0 sm:size-full sm:object-cover sm:object-right"
        />
      </picture>
      <div className="min-w-0 flex-1 px-4 pb-6 text-center sm:p-8 sm:text-left">
        <h2 className="text-md font-serif text-black sm:text-xl">Hungry right now?</h2>
        <p className="text-ds-gray-light-700 mt-2 text-sm sm:text-base sm:text-black">
          Dine Now at venues you can just walk into.{' '}
          <span className="sm:block">Available on First Table app now</span>
        </p>
        <Link
          href={DINE_NOW_PATH}
          kind="teal"
          scale="md"
          width="wide"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 rounded-2xl sm:w-fit"
          onClick={() => track('dine_now_banner_learn_more_clicked', eventProps)}>
          Learn more
        </Link>
      </div>
      <DismissButton
        label="Dismiss Dine Now"
        onClick={() => {
          track('dine_now_banner_dismissed', eventProps);
          onDismiss();
        }}
      />
    </section>
  );
}
